import {
  SESSION_FILES,
  SESSION_FILES_UPLOAD,
} from "../../../constants/constant";
import apiConfig, { bookingAPIKey } from "../../../service/apiConfig";
import { sessionStorageUtils } from "../../../utility/utility";

// Convert blob string into a File object
function convertBlobStringToFile(blobString: string, fileName: string): File {
  const byteString = atob(blobString.split(",")[1]);
  const mimeString = blobString.split(",")[0].split(":")[1].split(";")[0];

  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeString });
  return new File([blob], fileName, { type: mimeString });
}
interface PresignedUrlUploaderProps {
  setIsLoading: (isLoading: boolean) => void;
  images?: any[];
}

const filesUpload = [];
const fetchPresignedUrlAndUpload = async (
  fileName: string,
  apiKey: string,
  blob: string,
  setIsLoading: (isLoading: boolean) => void
): Promise<void> => {
  setIsLoading(true);
  const response = await fetch(
    `${apiConfig.presignedUrl}?filename=${fileName}&apikey=${apiKey}`
  );
  if (!response.ok) {
    setIsLoading(false);
    throw new Error(`Failed to fetch presigned URL: ${response.statusText}`);
  }
  const data = await response.json();
  if (data.presignedURL) {
    const file = convertBlobStringToFile(blob, fileName);
    await uploadMakeS3File(data.presignedURL, file);
    sessionStorageUtils.setItem(SESSION_FILES_UPLOAD, filesUpload);
  }
  setIsLoading(false);
};

const fetchPresignedUrl = async (
  setIsLoading: (isLoading: boolean) => void,
  images
): Promise<void> => {
  try {
    const fileUrls = sessionStorageUtils.getItem("fileUrls");
    const fileNames = fileUrls ? JSON.parse(fileUrls) : null;
    if (!fileNames) return;
    const apiKey = bookingAPIKey[process.env.NODE_ENV];
    const uploadFiles = getUploadFiles(images);
    await processFileUploads(fileNames, uploadFiles, apiKey, setIsLoading);
  } catch (error) {
    setIsLoading(false);
    console.error("Error fetching presigned URL:", error);
  }
};

const getUploadFiles = (images) => {
  return images
    ? images.map((img) => img.data)
    : JSON.parse(sessionStorageUtils.getItem(SESSION_FILES));
};

const processArrayFileNames = async (
  fileNames: string[],
  uploadFiles,
  apiKey: string,
  setIsLoading: (isLoading: boolean) => void
) => {
  for (let i = 0; i < fileNames.length; i++) {
    const fileName = fileNames[i];
    const blob = uploadFiles[i];
    await fetchPresignedUrlAndUpload(fileName, apiKey, blob, setIsLoading);
  }
};

const processObjectFileNames = async (
  fileNames: object,
  uploadFiles,
  apiKey: string,
  setIsLoading: (isLoading: boolean) => void
) => {
  for (const key in fileNames) {
    if (fileNames.hasOwnProperty(key)) {
      const fileName = fileNames[key];
      const blob = uploadFiles[key];
      await fetchPresignedUrlAndUpload(fileName, apiKey, blob, setIsLoading);
    }
  }
};

const processFileUploads = async (
  fileNames: object,
  uploadFiles,
  apiKey: string,
  setIsLoading: (isLoading: boolean) => void
) => {
  if (Array.isArray(fileNames)) {
    await processArrayFileNames(fileNames, uploadFiles, apiKey, setIsLoading);
  } else if (typeof fileNames === "object") {
    await processObjectFileNames(fileNames, uploadFiles, apiKey, setIsLoading);
  }
};

const uploadMakeS3File = async (url: string, file: File): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file: ${response.statusText}`);
    }
    filesUpload.push(file.name);
  } catch (error) {
    console.error("Error uploading file:", error);
  }
};

export const PresignedUrlUploader = async ({
  setIsLoading,
  images,
}: PresignedUrlUploaderProps): Promise<void> => {
  await fetchPresignedUrl(setIsLoading, images);
};
